// Registry mapping cosmetic assetKeys (store item metadata) to bundled SVG URLs.
// Unknown badge keys return undefined — callers render a lucide fallback.
// Unknown *banner* keys fall back to a generic contour banner so an equipped
// banner is never silently invisible (registry drift ≠ nothing rendered).
// Frames and stamps behave like badges: unknown keys return undefined and the
// caller simply renders nothing (both are pure overlays).

import compassRose from "../assets/cosmetics/badges/compass-rose.svg";
import trigBagger from "../assets/cosmetics/badges/trig-bagger.svg";
import nightOwl from "../assets/cosmetics/badges/night-owl.svg";
import firstLight from "../assets/cosmetics/badges/first-light.svg";
import ridgeline from "../assets/cosmetics/badges/ridgeline.svg";
import southDownsFinisher from "../assets/cosmetics/badges/south-downs-finisher.svg";
import foundingSupporterBadge from "../assets/cosmetics/badges/founding-supporter-badge.svg";
import trailblazer from "../assets/cosmetics/badges/trailblazer.svg";
import bogTrotter from "../assets/cosmetics/badges/bog-trotter.svg";
import allWeather from "../assets/cosmetics/badges/all-weather.svg";
import summitCairn from "../assets/cosmetics/badges/summit-cairn.svg";
import coastalDrifter from "../assets/cosmetics/badges/coastal-drifter.svg";
import scrambler from "../assets/cosmetics/badges/scrambler.svg";
import longestDay from "../assets/cosmetics/badges/longest-day.svg";
import wainwrightFinisher from "../assets/cosmetics/badges/wainwright-finisher.svg";
import threePeaksYorkshire from "../assets/cosmetics/badges/three-peaks-yorkshire.svg";
import pennineWayFinisher from "../assets/cosmetics/badges/pennine-way-finisher.svg";
import crestLakeDistrict from "../assets/cosmetics/badges/crest-lake-district.svg";
import crestSnowdonia from "../assets/cosmetics/badges/crest-snowdonia.svg";
import crestPeakDistrict from "../assets/cosmetics/badges/crest-peak-district.svg";
import crestYorkshireDales from "../assets/cosmetics/badges/crest-yorkshire-dales.svg";
import crestCairngorms from "../assets/cosmetics/badges/crest-cairngorms.svg";
import crestDartmoor from "../assets/cosmetics/badges/crest-dartmoor.svg";
import munroFinisher from "../assets/cosmetics/badges/munro-finisher.svg";
import welsh3000s from "../assets/cosmetics/badges/welsh-3000s.svg";
import coastToCoastFinisher from "../assets/cosmetics/badges/coast-to-coast-finisher.svg";
import hadriansWallFinisher from "../assets/cosmetics/badges/hadrians-wall-finisher.svg";
import wildCamper from "../assets/cosmetics/badges/wild-camper.svg";
import woodlandWanderer from "../assets/cosmetics/badges/woodland-wanderer.svg";
import waterfallHunter from "../assets/cosmetics/badges/waterfall-hunter.svg";
import goldenLarch from "../assets/cosmetics/badges/golden-larch.svg";
import crestBreconBeacons from "../assets/cosmetics/badges/crest-brecon-beacons.svg";
import crestPembrokeshireCoast from "../assets/cosmetics/badges/crest-pembrokeshire-coast.svg";
import betaProofSheet from "../assets/cosmetics/badges/beta-proof-sheet.svg";

import cartographersDesk from "../assets/cosmetics/banners/cartographers-desk.svg";
import alpenglow from "../assets/cosmetics/banners/alpenglow.svg";
import contourLines from "../assets/cosmetics/banners/contour-lines.svg";
import duskRidge from "../assets/cosmetics/banners/dusk-ridge.svg";
import foundingSupporterBanner from "../assets/cosmetics/banners/founding-supporter-banner.svg";
import fallbackBanner from "../assets/cosmetics/banners/fallback.svg";
import midsummerRidge from "../assets/cosmetics/banners/midsummer-ridge.svg";
import seaFret from "../assets/cosmetics/banners/sea-fret.svg";
import heatherMoor from "../assets/cosmetics/banners/heather-moor.svg";
import cloudInversion from "../assets/cosmetics/banners/cloud-inversion.svg";
import mapMargin from "../assets/cosmetics/banners/map-margin.svg";
import winterRidge from "../assets/cosmetics/banners/winter-ridge.svg";
import bothyLight from "../assets/cosmetics/banners/bothy-light.svg";
import larchSeason from "../assets/cosmetics/banners/larch-season.svg";
import caledonianPines from "../assets/cosmetics/banners/caledonian-pines.svg";
import auroraNight from "../assets/cosmetics/banners/aurora-night.svg";
import betaProvisionalEdition from "../assets/cosmetics/banners/beta-provisional-edition.svg";

import frameRopeCoil from "../assets/cosmetics/frames/frame-rope-coil.svg";
import frameCarabiner from "../assets/cosmetics/frames/frame-carabiner.svg";
import frameSummitLaurel from "../assets/cosmetics/frames/frame-summit-laurel.svg";
import frameContour from "../assets/cosmetics/frames/frame-contour.svg";
import frameCompassBezel from "../assets/cosmetics/frames/frame-compass-bezel.svg";
import frameBenchmark from "../assets/cosmetics/frames/frame-benchmark.svg";
import frameBetaRegistration from "../assets/cosmetics/frames/frame-beta-registration.svg";

import stampBootPrint from "../assets/cosmetics/stamps/stamp-boot-print.svg";
import stampSummitPost from "../assets/cosmetics/stamps/stamp-summit-post.svg";
import stampWaxSeal from "../assets/cosmetics/stamps/stamp-wax-seal.svg";
import stampCairn from "../assets/cosmetics/stamps/stamp-cairn.svg";
import stampBenchmark from "../assets/cosmetics/stamps/stamp-benchmark.svg";
import stampBetaProof from "../assets/cosmetics/stamps/stamp-beta-proof.svg";

const BADGE_ASSETS: Record<string, string> = {
  "compass-rose": compassRose,
  "trig-bagger": trigBagger,
  "night-owl": nightOwl,
  "first-light": firstLight,
  "ridgeline": ridgeline,
  "south-downs-finisher": southDownsFinisher,
  "founding-supporter-badge": foundingSupporterBadge,
  "trailblazer": trailblazer,
  "bog-trotter": bogTrotter,
  "all-weather": allWeather,
  "summit-cairn": summitCairn,
  "coastal-drifter": coastalDrifter,
  "scrambler": scrambler,
  "longest-day": longestDay,
  "wainwright-finisher": wainwrightFinisher,
  "three-peaks-yorkshire": threePeaksYorkshire,
  "pennine-way-finisher": pennineWayFinisher,
  "crest-lake-district": crestLakeDistrict,
  "crest-snowdonia": crestSnowdonia,
  "crest-peak-district": crestPeakDistrict,
  "crest-yorkshire-dales": crestYorkshireDales,
  "crest-cairngorms": crestCairngorms,
  "crest-dartmoor": crestDartmoor,
  "munro-finisher": munroFinisher,
  "welsh-3000s": welsh3000s,
  "coast-to-coast-finisher": coastToCoastFinisher,
  "hadrians-wall-finisher": hadriansWallFinisher,
  "wild-camper": wildCamper,
  "woodland-wanderer": woodlandWanderer,
  "waterfall-hunter": waterfallHunter,
  "golden-larch": goldenLarch,
  "crest-brecon-beacons": crestBreconBeacons,
  "crest-pembrokeshire-coast": crestPembrokeshireCoast,
  "beta-proof-sheet": betaProofSheet,
};

const BANNER_ASSETS: Record<string, string> = {
  "cartographers-desk": cartographersDesk,
  "alpenglow": alpenglow,
  "contour-lines": contourLines,
  "dusk-ridge": duskRidge,
  "founding-supporter-banner": foundingSupporterBanner,
  "midsummer-ridge": midsummerRidge,
  "sea-fret": seaFret,
  "heather-moor": heatherMoor,
  "cloud-inversion": cloudInversion,
  "map-margin": mapMargin,
  "winter-ridge": winterRidge,
  "bothy-light": bothyLight,
  "larch-season": larchSeason,
  "caledonian-pines": caledonianPines,
  "aurora-night": auroraNight,
  "beta-provisional-edition": betaProvisionalEdition,
};

const FRAME_ASSETS: Record<string, string> = {
  "frame-rope-coil": frameRopeCoil,
  "frame-carabiner": frameCarabiner,
  "frame-summit-laurel": frameSummitLaurel,
  "frame-contour": frameContour,
  "frame-compass-bezel": frameCompassBezel,
  "frame-benchmark": frameBenchmark,
  "frame-beta-registration": frameBetaRegistration,
};

const STAMP_ASSETS: Record<string, string> = {
  "stamp-boot-print": stampBootPrint,
  "stamp-summit-post": stampSummitPost,
  "stamp-wax-seal": stampWaxSeal,
  "stamp-cairn": stampCairn,
  "stamp-benchmark": stampBenchmark,
  "stamp-beta-proof": stampBetaProof,
};

export function getBadgeAsset(assetKey: string | undefined): string | undefined {
  if (!assetKey) return undefined;
  return BADGE_ASSETS[assetKey];
}

export function getBannerAsset(assetKey: string | undefined): string | undefined {
  if (!assetKey) return undefined;
  return BANNER_ASSETS[assetKey] ?? fallbackBanner;
}

export function getFrameAsset(assetKey: string | undefined): string | undefined {
  if (!assetKey) return undefined;
  return FRAME_ASSETS[assetKey];
}

export function getStampAsset(assetKey: string | undefined): string | undefined {
  if (!assetKey) return undefined;
  return STAMP_ASSETS[assetKey];
}
